/**
 * What a person is told when the API refuses, for every status that is not 409.
 *
 * 409 has had this treatment since wi-a04cab6b and its own guard file; the
 * enumeration stopped there, so 400, 401, 403, 404 and 500 never got one. The
 * result was not a missing sentence but a WRONG one, which is harder to notice:
 * the ENVELOPE has copy, so a specific code with none silently resolves to it.
 * `platform_capability_required` -- refused because you lack a capability, on a
 * form where you were CREATING something -- fell through to `forbidden` and
 * read "You don't have permission to view this." Nothing was blank, so nothing
 * looked broken.
 *
 * `refusal-copy.test.ts` enumerates the codes FROM THE API SOURCE and fails if
 * one has no sentence of its own, so this table cannot fall behind the API and
 * cannot quietly hide behind an envelope again.
 *
 * The table lived inside ui.tsx until 2026-09-06. It moved here whole, because
 * a second table beside the first is the thing that goes stale and starts
 * lying -- the failure this lane has spent two days closing.
 */

import {
  ERROR_CODE_BRAND_NOT_FOUND,
  ERROR_CODE_NO_ACTIVE_BRAND,
  ERROR_CODE_REGION_NOT_IN_BRAND,
} from './brand-wire';

export const ERROR_MESSAGES: Record<string, string> = {
  // PDF download refusals. The renderer never retries and never overspends, so
  // every one of these is a normal, expected answer rather than a fault — the copy
  // says what happened and what the person can do, and never asks them to retry a
  // thing that will refuse again. The repo-wide refusal scanner cannot see these
  // (the reason reaches `err()` in a variable), so pdf-refusal-copy.test.ts pins
  // them instead. Keep the two in step.
  pdf_unavailable: 'The PDF could not be produced just now. The report itself is unaffected.',
  cap_exhausted:
    "This month's PDF allowance has been used up. It resets at the start of next month — the report still goes out by email as usual.",
  no_cap_configured:
    'PDF downloads are switched off. An administrator has to set the monthly allowance before this can work.',
  job_disabled: 'This kind of render is switched off deliberately.',
  no_api_token:
    'PDF downloads are not fully set up yet. An administrator needs to finish configuring them.',
  not_authorised:
    'PDF downloads are not permitted yet. An administrator has to give the PDF service permission before this can work; retrying will not help until then.',
  ledger_unreadable:
    "We couldn't check this month's PDF allowance, so nothing was rendered. Try again shortly.",
  render_failed: 'Building the PDF failed. The report itself is unaffected — try again shortly.',
  // Auth
  unauthorized: 'Please sign in again to continue.',
  forbidden: "You don't have permission to view this.",
  not_platform_admin: 'Platform admin access required.',
  user_not_found: "We couldn't find an account for that email.",
  user_inactive: 'This account is no longer active. Please contact your account manager.',
  invalid_credentials: 'Email or password is incorrect.',
  invalid_credentials_remaining: 'Email or password is incorrect.',
  account_locked:
    'Too many failed attempts — your account is locked for 15 minutes. Use the email magic-link option below to sign in instead.',
  invalid_email: 'Please enter a valid email address.',
  invalid_role: "That's not a recognized role.",
  email_taken: 'That email is already registered.',
  // "Please request a new one" named a control the product does not have —
  // there is no resend button anywhere. These now name the action that exists
  // on the page they are shown on: entering the address again. On /login that
  // genuinely mints a fresh link; a signup verification no longer lands here at
  // all, because there it would not have (auth/verify.ts routes by purpose).
  invalid_token: 'This sign-in link is no longer valid. Enter your email below to get a new one.',
  token_expired: 'This sign-in link has expired. Enter your email below to get a new one.',
  token_already_used:
    'This sign-in link has already been used. Enter your email below to get a new one.',
  missing_token: 'The sign-in link is incomplete. Enter your email below to get a new one.',
  // Shown on /signup, where re-submitting the form re-sends the verification
  // mail for an unverified request (auth/signup.ts returns early only for an
  // approved or rejected one).
  verify_link_expired:
    'That verification link has expired. Enter your email again and we will send a new one.',
  missing_bearer: 'Please sign in again to continue.',
  password_too_short: 'Please choose a password with at least 10 characters.',
  password_too_long: "That's a bit too long — keep it under 200 characters.",
  current_password_required: 'Enter your current password to change it.',
  passwords_dont_match: "The two passwords don't match.",
  // Generic
  not_found: "We couldn't find what you were looking for.",
  http_error: 'Something went wrong. Please try again.',
  unknown: 'Something went wrong. Please try again.',
  internal_error: 'Something went wrong on our end. Please try again in a moment.',
  network_error: 'Network unavailable. Check your connection and try again.',
  rate_limited: 'Too many requests. Slow down a moment.',
  invalid_input: 'Some fields are invalid. Check the form and try again.',
  invalid_json:
    "That didn't arrive as valid data. Reload the page and try again — if it keeps happening, tell an Adholics admin.",
  invalid_payload: 'That webhook payload must be a JSON object.',
  bad_request: "We didn't understand that request. Please try again.",
  // Brand + onboarding
  [ERROR_CODE_BRAND_NOT_FOUND]: "We couldn't find that workspace.",
  slug_taken: 'That URL handle is already in use.',
  slug_invalid: 'Use lowercase letters, numbers and dashes only.',
  invalid_slug: 'Use lowercase letters, numbers and dashes only.',
  cannot_remove_self_from_platform: "You can't remove yourself from the platform workspace.",
  cannot_archive_platform: "The platform workspace can't be archived.",
  // Data + analytics
  [ERROR_CODE_REGION_NOT_IN_BRAND]: "This workspace doesn't have data for that region yet.",
  no_data_sources: 'No data sources connected for this region yet.',
  no_leads_for_region: 'No leads source connected for this region yet.',
  no_leads_source: 'No leads source connected for this region yet.',
  no_pacing_sheet:
    'No budget targets set for this region + month yet. An admin can add them in Admin \u2192 Pacing.',
  all_tabs_empty: 'Data sources are connected but returned no rows.',
  fetch_failed: "Couldn't reach the upstream data source.",
  data_source_unreachable:
    "This report couldn't read its data source. An Adholics admin needs to check the connection.",
  country_required: 'Please select a region first.',
  contact_required: 'Send at least one contact field so the lead can be deduplicated.',
  month_required: 'Please choose a month.',
  month_format_yyyy_mm: 'Month should be formatted as YYYY-MM.',
  invalid_sheet: "That doesn't look like a valid Google Sheets URL.",
  // Bulk
  no_invites: 'Add at least one email address to invite.',
  // These two are per-ROW outcomes of a bulk invite, so they are read next to
  // forty-nine others and have to say which line and what to do about it.
  // Both fell to the mechanical fallback until 2026-09-06 ("Missing email.").
  missing_email: 'That line has no email address on it.',
  invite_failed:
    "That invite couldn't be sent. Try that address on its own to see what went wrong.",
  too_many_invites_max_50: 'Please send invites in batches of 50 or fewer.',
  // Alerts
  no_delivery_channel: 'Add at least one email recipient or a Slack webhook.',
  no_valid_recipients: 'Add at least one valid email address.',
  invalid_slack_webhook: "That doesn't look like a Slack incoming-webhook URL.",
  invalid_threshold: 'Threshold must be a number.',
  invalid_metric: "That metric isn't supported yet.",
  invalid_op: "Comparison must be 'above' or 'below'.",
  no_ids: 'No items selected.',
  too_many_ids_max_50: 'Please process in batches of 50 or fewer.',
  // Domains
  domain_taken: 'That subdomain is already in use.',
  domain_invalid: 'That hostname looks invalid.',
  // OAuth
  oauth_not_configured: "This integration isn't configured yet — talk to your account manager.",
  oauth_callback_not_implemented: 'This integration is in preview and not ready yet.',
  // Billing
  plan_limit_exceeded: "Your plan doesn't include this — upgrade in /billing to unlock.",
  insufficient_plan: "Your plan doesn't include this — upgrade in /billing to unlock.",
  [ERROR_CODE_NO_ACTIVE_BRAND]: 'No active workspace. Switch workspaces and try again.',
  no_stripe_customer: 'Stripe customer not set up yet — start a checkout from /billing first.',
  enterprise_is_sales_led: 'Enterprise plans are activated by our team — email sales@adholics.net.',
  unknown_plan: "We couldn't find that plan.",
  stripe_not_configured: 'Billing is being set up — please try again shortly.',
  stripe_checkout_failed: 'Stripe rejected the checkout request. Please try again.',
  stripe_portal_failed: "Couldn't open the billing portal. Please try again.",
  // Documents
  file_too_large: 'That file is too large to upload (limit 10 MB).',
  unsupported_file_type: "That file type isn't supported.",
  // ─── 403 · refused because of WHO you are, not what you asked for ────────
  // Every one of these fell through to `forbidden`, which says "view".
  // Half of them are raised on writes, where "view" is simply wrong.
  platform_capability_required:
    'Your Adholics staff role does not carry the permission this action needs. Ask an Adholics admin to grant it.',
  insufficient_role:
    'Your role in this workspace does not allow this. An owner or admin can do it, or can raise your role.',
  not_a_member: 'You are not a member of this workspace.',
  tenant_inactive: 'This workspace is not active, so it cannot be changed.',
  subscription_inactive:
    'This workspace has no active subscription, so this is switched off. Check Billing.',
  pii_elevation_required:
    'Personal data needs approved access. Request it, and this will work once it is granted.',
  only_the_owning_organisation_may_delete_data:
    'Only the organisation that owns this workspace can delete its data. A partner agency cannot.',
  handover_date_is_the_payers_to_set:
    'Only the organisation that pays for this workspace can set the handover date.',
  // The API spells this both ways; tenant_not_found already has a sentence.
  brand_not_found: "We couldn't find that workspace.",

  // ─── 404 · we looked and it is not there ─────────────────────────────────
  // All of these read "We couldn't find what you were looking for", which is
  // true and useless: it never says WHICH thing is missing, and several of
  // these are not "missing" at all (archived, unpublished, revoked).
  alert_not_found: "We couldn't find that alert. It may have been deleted.",
  connection_not_found: "We couldn't find that connection. It may have been disconnected.",
  contract_not_found: 'This workspace has no contract on file yet.',
  dashboard_not_found: "We couldn't find that dashboard. It may have been deleted.",
  dashboard_archived: 'This dashboard has been archived, so it can no longer be opened.',
  dashboard_not_published: 'This dashboard has no published version yet.',
  data_source_not_found: "We couldn't find that data source. It may have been removed.",
  document_not_found: "We couldn't find that document. It may have been deleted.",
  domain_not_found: "We couldn't find that domain on this workspace.",
  draft_not_found: 'There is no saved draft for this dashboard.',
  grant_not_found: "We couldn't find that share. It may already have been withdrawn.",
  grantee_org_not_found: "We couldn't find that partner organisation.",
  lead_webhook_not_found: 'This workspace does not have a lead webhook yet.',
  report_not_found: "We couldn't find that report. It may have been deleted.",
  signup_not_found: "We couldn't find that access request. It may already have been decided.",
  to_org_not_found: "We couldn't find the organisation you are transferring to.",
  transfer_not_found: "We couldn't find that transfer.",
  version_not_found: "We couldn't find that version.",
  widget_not_found: "We couldn't find that panel on this dashboard.",
  no_elevation: 'You have no approved access to personal data at the moment.',
  no_pointer: 'This dashboard has no current version set.',
  no_published_version: 'This dashboard has never been published.',
  no_rows_for_source_month: 'That data source has no rows for the month you asked for.',
  object_missing: 'The stored file is missing. Ask an Adholics admin to look into it.',
  unknown_provider: "That isn't a provider Adholics supports.",
  // The two the public embed page needs, and could not reach until today.
  token_not_found: 'This shared link is not valid. Ask whoever sent it for a new one.',
  token_revoked: 'This shared link has been turned off. Ask whoever sent it for a new one.',

  // ─── 401 · sign-in ───────────────────────────────────────────────────────
  missing_refresh: 'Your session has ended. Please sign in again.',
  session_revoked: 'This session was signed out. Please sign in again.',
  invalid_code: 'That code is not right. Check your authenticator app and try again.',
  invalid_challenge: 'That verification step has expired. Start signing in again.',

  // ─── 400 · the request itself ────────────────────────────────────────────
  // Missing values, named individually. "We didn't understand that request"
  // was the answer to every one of them.
  name_required: 'Please give this a name.',
  contact_email_required: 'Please give a contact email address.',
  plan_not_editable_here:
    "This workspace's plan comes from its billing contract. Change it there, not on this form.",
  tenant_id_required: 'Please choose a workspace first.',
  dashboard_id_required: 'Please choose a dashboard first.',
  plan_id_required: 'Please choose a plan.',
  grantee_org_required: 'Please choose the partner organisation to share with.',
  to_org_required: 'Please choose the organisation to transfer to.',
  reason_required: 'Please say why — a reason is required here.',
  reporting_timezone_required:
    'Please set this workspace\u2019s reporting time zone before scheduling anything.',
  layout_required: 'This dashboard has no layout to save.',
  widget_required: 'Please choose a panel first.',
  sample_required: 'Please enter a sample name to test against.',
  canonical_required: 'Please give the canonical name this maps to.',
  file_required: 'Please choose a file to upload.',
  missing_params: 'The webhook URL is missing its workspace or secret parameter.',
  rows_array_required: 'Nothing to save — no rows were sent.',
  ranges_required: 'Please choose at least one date range.',
  range_key_required: 'Each date range needs a name.',
  messages_required: 'Please type a message first.',
  missing_fields: 'Some required fields are empty. Fill them in and try again.',
  missing_tenant_id: 'Please choose a workspace first.',
  missing_code: 'That link is incomplete. Start the connection again.',
  missing_code_or_state: 'That link is incomplete. Start the connection again.',
  missing_signature: 'That request is missing its signature and was not accepted.',
  external_account_id_required: 'Please choose which ad account to use.',
  external_account_id_missing: 'Please choose which ad account to use.',
  gid_required_for_leads: 'A leads sheet needs the tab id (gid) from its URL.',
  tab_name_missing: 'Please give the tab name inside the spreadsheet.',
  tab_name_required_for_perf: 'A performance sheet needs the tab name inside the spreadsheet.',
  tab_name_required_for_pacing: 'A pacing sheet needs the tab name inside the spreadsheet.',
  asset_id_required: 'Please choose which item to share.',
  preset_id_string_required: 'Please choose a preset.',
  features_object_required: 'Features must be sent as a set of on/off values.',
  overrides_object_required: 'Overrides must be sent as a set of on/off values.',
  theme_must_be_json_object: 'The theme must be a set of settings, not plain text.',
  // Things that are the wrong shape or the wrong value.
  invalid_action: "That isn't an action we recognise.",
  invalid_alias: "That alias isn't valid.",
  invalid_asset_type: "That isn't a kind of item that can be shared.",
  invalid_cadence: "That isn't a schedule we support.",
  invalid_content: 'That content is empty or not readable.',
  invalid_hostname: 'That hostname looks invalid.',
  invalid_kind: "That isn't a data source type we support.",
  invalid_scope: "That isn't a sharing scope we recognise.",
  invalid_segments: 'The naming segments are not valid. Check the editor and try again.',
  invalid_signature: 'That request could not be verified and was not accepted.',
  invalid_state: 'That connection link has expired. Start the connection again.',
  invalid_status: "That isn't a status this can be set to.",
  invalid_timezone: "That isn't a time zone we recognise. Pick one from the list.",
  invalid_preview_convention: 'That naming convention is not valid, so it cannot be previewed.',
  layout_invalid: 'This dashboard layout is not valid, so it cannot be saved.',
  unsupported_provider: "That isn't a provider Adholics supports.",
  method_not_allowed: "That isn't something this page can do.",
  expected_multipart: 'That upload was not sent correctly. Try choosing the file again.',
  // Sizes and limits.
  file_empty: 'That file is empty.',
  data_too_large: 'That is too much data to save at once.',
  body_too_short: 'Please write a little more.',
  message_too_long: 'That message is too long — please shorten it.',
  reason_too_long: 'That reason is too long — please shorten it.',
  too_many_messages: 'That is too many messages at once.',
  too_many_recipients: 'That is too many recipients — please send to fewer people.',
  minutes_out_of_range: 'Please choose a number of minutes inside the allowed range.',
  // Dates and months.
  month_must_be_yyyy_mm: 'Month should be formatted as YYYY-MM.',
  malformed_date: "That date isn't valid. Use YYYY-MM-DD.",
  malformed_compare_date: "That comparison date isn't valid. Use YYYY-MM-DD.",
  range_from_after_to: 'The start of that range is after its end.',
  compare_from_after_to: 'The start of the comparison range is after its end.',
  // States that are simply already true, or not applicable.
  already_current: 'That is already the current version.',
  already_enabled: 'That is already switched on.',
  nothing_to_update: 'Nothing was changed.',
  not_enabled: 'That is not switched on for this workspace.',
  no_pending_enrollment: 'There is nothing waiting to be set up.',
  handover_date_not_applicable: 'A handover date does not apply to this transfer.',
  to_org_is_from_org: 'That workspace already belongs to this organisation.',
  connection_revoked: 'That connection has been disconnected. Connect it again.',
  connection_tenant_mismatch: 'That connection belongs to a different workspace.',
  asset_on_brand_scope: 'A whole-workspace share cannot also name a single item.',
  test_only_supported_for_perf_v1: 'Only performance sheets can be tested here.',
  // Reserved and protected things.
  reserved_slug: 'That URL handle is reserved. Please choose a different one.',
  status_platform_reserved: 'That status is reserved for the Adholics workspace.',
  status_reserved_for_platform_tenant: 'That status is reserved for the Adholics workspace.',
  status_not_settable: 'That status cannot be set by hand.',
  cannot_delete_platform_tenant: "The Adholics workspace can't be deleted.",
  cannot_modify_platform_tenant_core_fields:
    "The Adholics workspace's core details can't be changed.",
  confirm_must_match_workspace_slug:
    "That doesn't match the workspace handle. Type it exactly to confirm.",
  // Unknown things named in a request.
  unknown_feature: "That isn't a feature we recognise.",
  unknown_preset: "That isn't a preset we recognise.",
  feature_not_boolean: 'A feature has to be on or off.',
  invalid_countries: 'Lead webhook countries must be country codes such as NDL or BE.',

  version_or_version_id_required: 'Please choose which version to roll back to.',
  from_month_must_be_yyyy_mm: 'The month you are copying from should be formatted as YYYY-MM.',
  to_month_must_be_yyyy_mm: 'The month you are copying to should be formatted as YYYY-MM.',
  signup_already_decided: 'That access request has already been decided.',
  payload_too_large: 'That webhook payload is too large. Send 16 KB or less.',

  // Public lead webhook. Same sentence for absent rows and bad secrets, so the
  // caller cannot distinguish a known workspace from a guessed one.
  unknown_webhook: "We couldn't find that webhook. Check the URL and secret, then try again.",

  // ─── 500 · genuinely ours, and each says who can fix it ──────────────────
  doc_encryption_key_not_set:
    'Document storage is not configured yet. An Adholics admin needs to set it up.',
  encryption_key_not_set: 'Encryption is not configured yet. An Adholics admin needs to set it up.',
  decrypt_failed:
    'That document could not be decrypted, so it cannot be downloaded. Ask an Adholics admin to look into it.',
  insert_succeeded_but_read_failed:
    'That saved, but we could not read it back. Reload the page to check.',
};
